// SPDX-License-Identifier: LicenseRef-Pachamama-Proprietary
// Copyright 2026 Pachamama Studios B.V. All rights reserved.

/**
 * AYNI — talking with the AYNI chatbot (AYNI 3D's `docs/AI_CHATBOT_VOICE.md`),
 * on the page and in the headset. The voice mode itself is AYNI 3D's
 * (`src/ayni3d/renderer/ui/ai-voice-mode.ts`, copied unchanged): one tap
 * starts it, the mic hears when the question is over, the words go in as an
 * ordinary question (the panel draws it), and the reply is read aloud; then
 * it listens again, until it is tapped off or nobody speaks. A tap while it
 * speaks interrupts it.
 *
 * The words become text on this site's own route (`/api/ayni-chat/audio/
 * transcriptions`); the reply is read by the route's voice (English and
 * Spanish) or, for another language or when that fails, by the device's own
 * voice; with neither, the captions still show. The headset's dock draws the
 * phase and the caption (`vrHud.ts` VrVoiceState), so the chatbot works in
 * VR, where there is no keyboard.
 */
import { VoiceMode, type VoiceEndReason, type VoicePhase } from '../ayni3d/renderer/ui/ai-voice-mode'
import { DeviceVoice, SpeechPlayer, SpeechRecorder, isPhantomTranscript, speechInputSupported } from '../ayni3d/renderer/ui/ai-speech-io'
import { toSpeakableText } from '../ayni3d/shared/ai-speakable-text'
import type { AiChatLanguage } from '../ayni3d/shared/ai-languages'
import type { VrVoiceState } from '../services/vrHud'
import { AYNI_CHAT_BASE_URL } from './xrChatScope'

export interface AyniChatVoiceHost {
  language(): AiChatLanguage
  /** Whether this account may talk to the chatbot now. */
  allowed(): boolean
  /** Asks the question as an ordinary turn (the panel draws it): the reply's words, or null. */
  ask(text: string): Promise<string | null>
  /** Stops the answer in progress. */
  cancel(): void
  /** The phase changed: the panel's mic and status line. */
  onPhase?(phase: VoicePhase, detail?: { ended?: VoiceEndReason; code?: string }): void
  fetchImpl?: typeof fetch
  /** For tests. */
  now?(): number
}

export interface AyniChatVoice {
  /** Whether this browser can record (a secure page with a mic). */
  readonly supported: boolean
  tap(): void
  stop(): void
  phase(): VoicePhase
  /** What the headset's dock shows: null hides its mic. */
  vrState(): VrVoiceState | null
}

/** How long a finished reply stays on the dock's caption strip, and an error's notice. */
const REPLY_LINGER_MS = 10_000
const ERROR_LINGER_MS = 5_000

/** The route's answer as the engine's error codes. */
function codeFor(status: number): string {
  if (status === 429) return 'AI_QUOTA'
  if (status === 401) return 'AI_NOT_CONFIGURED'
  return 'AI_UNAVAILABLE'
}

export function createAyniChatVoice(host: AyniChatVoiceHost): AyniChatVoice {
  const fetchImpl: typeof fetch = host.fetchImpl ?? ((input, init) => fetch(input, init))
  const now = (): number => host.now?.() ?? Date.now()
  const recorder = new SpeechRecorder()
  const device = typeof window !== 'undefined' && 'speechSynthesis' in window ? new DeviceVoice() : null
  let question = ''
  let reply = ''
  let replyAt = 0
  let ended: { reason: VoiceEndReason; at: number } | null = null

  const player = new SpeechPlayer(async (text, language) => {
    try {
      const res = await fetchImpl(`${AYNI_CHAT_BASE_URL}/audio/speech`, {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ input: toSpeakableText(text, language) || text, language }),
      })
      if (!res.ok) return { ok: false, code: codeFor(res.status), message: String(res.status) }
      return { ok: true, audio: new Uint8Array(await res.arrayBuffer()) }
    } catch {
      return { ok: false, code: 'AI_UNAVAILABLE', message: 'unreachable' }
    }
  }, () => {
    // No voice at all: the captions and the panel still carry the reply.
  }, device)

  const mode = new VoiceMode({
    record: (onEnd) => {
      player.stop()
      return recorder.start({ onLimit: () => onEnd('speech-end'), onEnd })
    },
    stopRecording: () => recorder.stop(),
    cancelRecording: () => recorder.cancel(),
    transcribe: async (wav) => {
      const form = new FormData()
      form.append('file', new Blob([new Uint8Array(wav)], { type: 'audio/wav' }), 'question.wav')
      form.append('language', host.language())
      try {
        const res = await fetchImpl(`${AYNI_CHAT_BASE_URL}/audio/transcriptions`, { method: 'POST', credentials: 'same-origin', body: form })
        if (!res.ok) return { ok: false, code: codeFor(res.status) }
        const body = (await res.json()) as { text?: unknown }
        return { ok: true, text: typeof body.text === 'string' ? body.text : '' }
      } catch {
        return { ok: false, code: 'AI_UNAVAILABLE' }
      }
    },
    ask: async (text) => {
      question = text
      reply = ''
      const answer = await host.ask(text)
      if (answer) {
        reply = answer
        replyAt = now()
      }
      return answer
    },
    speak: async (text) => {
      if (player.canSpeak(host.language())) await player.say(text, host.language())
      replyAt = now()
    },
    stopSpeaking: () => player.stop(),
    cancelTurn: () => host.cancel(),
    isNoise: (text) => isPhantomTranscript(text),
    onPhase: (phase, detail) => {
      if (phase === 'listening') {
        question = ''
        ended = null
      }
      if (detail?.ended && detail.ended !== 'quiet') ended = { reason: detail.ended, at: now() }
      host.onPhase?.(phase, detail)
    },
  }, { handsFree: true })

  const supported = speechInputSupported()
  return {
    supported,
    tap: () => {
      if (!supported || !host.allowed()) return
      mode.tap()
    },
    stop: () => mode.stop(),
    phase: () => mode.phase,
    vrState: () => {
      if (!supported || !host.allowed()) return null
      switch (mode.phase) {
        case 'listening':
          return { phase: 'listening', caption: '' }
        case 'transcribing':
          return { phase: 'thinking', caption: '' }
        case 'thinking':
          return { phase: 'thinking', caption: question }
        case 'speaking':
          return { phase: 'speaking', caption: reply }
        case 'off':
          if (ended && now() - ended.at < ERROR_LINGER_MS) return { phase: 'error', caption: '' }
          return { phase: 'idle', caption: reply && now() - replyAt < REPLY_LINGER_MS ? reply : '' }
      }
    },
  }
}
